import React, { useEffect, useState } from 'react';
import { TeacherApplicationForm, SubjectsAndSchools } from '../../model/TeacherApplicationForm';
import { useLoading } from '../../context/LoadingContext';
import { useError } from '../../context/ErrorContext';
import api from '../../utils/api';
import { loadSubjectIcons } from '../../utils/loadSvg';
import Loading from '../Loading';


interface SubjectsSelectionStepProps {
  formData: TeacherApplicationForm;
  setFormData: (data: TeacherApplicationForm) => void;
  errors: { [key: string]: string };
  setErrors: (errors: { [key: string]: string }) => void;
}

// Types from API response
interface Subject {
  id: number;
  name: string;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string | null;
}

interface School {
  id: number;
  name: string;
  level: string;
  school_type: string;
  classes: string[];
}

interface SchoolType {
  [schoolTypeName: string]: School[];
}

interface SchoolLevel {
  [levelName: string]: SchoolType;
}

interface SubjectInfo {
  subject: Subject;
  schools: SchoolLevel;
}

type Step = 'chooseSubject' | 'chooseSchool';

const getLevelDescription = (levelName: string): string => {
  switch (levelName) {
    case 'hs':
      return 'Scuole Superiori';
    case 'ms':
      return 'Scuole Medie';
    case 'ps':
      return 'Scuole Elementari';
    default:
      return levelName;
  }
};

const getSchoolTypeName = (schoolTypeName: string): string => {
  switch (schoolTypeName) {
    case 'liceo':
      return 'Licei';
    case 'ist_prof':
      return 'Istituto Professionale';
    case 'ist_tec':
    case 'tecnico':
      return 'Istituto Tecnico';
    case 'scuola_media':
    case 'medie':
      return 'Scuola Media';
    case 'scuola_elementare':
      return 'Scuola Elementare';
    default:
      return schoolTypeName;
  }
};

const SubjectsSelectionStep: React.FC<SubjectsSelectionStepProps> = ({ formData, setFormData, errors, setErrors }) => {
  const [subjects, setSubjects] = useState<SubjectInfo[]>([]);
  const [step, setStep] = useState<Step>('chooseSubject');
  const [selectedSubject, setSelectedSubject] = useState<SubjectInfo | null>(null);
  const [selectedClasses, setSelectedClasses] = useState<{ [schoolId: string]: string[] }>({});
  const [images, setImages] = useState<{ [key: string]: string }>({});
  const [imagesLoading, setImagesLoading] = useState(false);
  const { setLoading } = useLoading();
  const { setError } = useError();

  // Load subject images
  const loadSubjectImages = async (subjectsData: SubjectInfo[]) => {
    setImagesLoading(true);
    setImages(await loadSubjectIcons(subjectsData.map((subjectInfo) => subjectInfo.subject.name)));
    setImagesLoading(false);
  };

  // Clean empty schools
  const cleanEmptySchools = (subjects: SubjectsAndSchools[]): SubjectsAndSchools[] => {
    return subjects.map((subject) => ({
      ...subject,
      schools: subject.schools.filter((school) => school.classes.length > 0),
    }));
  };

  // Update formData helper
  const updateFormDataSubjects = (newSubjects: SubjectsAndSchools[]) => {
    const cleanedSubjects = cleanEmptySchools(newSubjects);
    setFormData({
      ...formData,
      subjects: cleanedSubjects,
    });

    // Clear error if selections exist
    if (cleanedSubjects.length > 0 && errors.subjects) {
      const newErrors = { ...errors };
      delete newErrors.subjects;
      setErrors(newErrors);
    }
  };

  // Fetch subjects from API
  useEffect(() => {
    const fetchSubjects = async () => {
      setLoading(true);
      try {
        const response = await api.get('/subject');
        const apiData = response.data;

        if (!apiData.subjects || apiData.subjects.length === 0) {
          setError(true, 'Nessuna materia disponibile.', 404);
          return;
        }

        setSubjects(apiData.subjects);
        await loadSubjectImages(apiData.subjects);
      } catch (error: any) {
        console.error('Error loading subjects:', error);
        setError(true, error.response?.data?.message || 'Errore nel caricamento delle materie. Riprova più tardi.', error.response?.status || 500);
      } finally {
        setLoading(false);
      }
    };

    fetchSubjects();
  }, [setError, setLoading]);

  // Handler: Select subject
  const handleSelectSubject = (subject: SubjectInfo) => {
    setSelectedSubject(subject);

    const currentSubjects = formData.subjects || [];
    const existingSubject = currentSubjects.find((item) => item.id === subject.subject.id);

    if (!existingSubject) {
      updateFormDataSubjects([...currentSubjects, { id: subject.subject.id, schools: [] }]);
    }

    setStep('chooseSchool');
  };

  // Handler: Toggle single class
  const handleSelectYears = (schoolId: number, schoolClass: string) => {
    if (!selectedSubject) return;

    const currentSubjects = [...(formData.subjects || [])];
    const subjectIndex = currentSubjects.findIndex((item) => item.id === selectedSubject.subject.id);

    if (subjectIndex === -1) {
      currentSubjects.push({
        id: selectedSubject.subject.id,
        schools: [{ id: schoolId, classes: [schoolClass] }],
      });
    } else {
      const updatedSchools = [...currentSubjects[subjectIndex].schools];
      const schoolIndex = updatedSchools.findIndex((school) => school.id === schoolId);

      if (schoolIndex === -1) {
        updatedSchools.push({
          id: schoolId,
          classes: [schoolClass],
        });
      } else {
        const updatedClasses = new Set(updatedSchools[schoolIndex].classes);

        if (updatedClasses.has(schoolClass)) {
          updatedClasses.delete(schoolClass);
        } else {
          updatedClasses.add(schoolClass);
        }

        updatedSchools[schoolIndex].classes = Array.from(updatedClasses);
      }

      currentSubjects[subjectIndex].schools = updatedSchools;
    }

    updateFormDataSubjects(currentSubjects);

    // Update UI state
    setSelectedClasses((prev) => {
      const currentClasses = prev[schoolId] || [];
      const isSelected = currentClasses.includes(schoolClass);

      return {
        ...prev,
        [schoolId]: isSelected ? currentClasses.filter((cls) => cls !== schoolClass) : [...currentClasses, schoolClass],
      };
    });
  };

  // Handler: Select/Deselect all classes for a school
  const handleSelectAllYears = (schoolId: number, classes: string[]) => {
    if (!selectedSubject) return;

    const currentSubjects = [...(formData.subjects || [])];
    const subjectIndex = currentSubjects.findIndex((item) => item.id === selectedSubject.subject.id);

    if (subjectIndex === -1) {
      currentSubjects.push({
        id: selectedSubject.subject.id,
        schools: [{ id: schoolId, classes: classes }],
      });
    } else {
      const updatedSchools = [...currentSubjects[subjectIndex].schools];
      const schoolIndex = updatedSchools.findIndex((school) => school.id === schoolId);

      if (schoolIndex === -1) {
        updatedSchools.push({
          id: schoolId,
          classes: classes,
        });
      } else {
        const isAllSelected = classes.every((cls) => updatedSchools[schoolIndex].classes.includes(cls));

        updatedSchools[schoolIndex].classes = isAllSelected ? [] : classes;
      }

      currentSubjects[subjectIndex].schools = updatedSchools;
    }

    updateFormDataSubjects(currentSubjects);

    // Update UI state
    setSelectedClasses((prev) => {
      const currentClasses = prev[schoolId] || [];
      const isAllSelected = classes.every((cls) => currentClasses.includes(cls));

      return {
        ...prev,
        [schoolId]: isAllSelected ? [] : classes,
      };
    });
  };

  // Handler: Go back to subject selection
  const handleBack = () => {
    setSelectedSubject(null);
    setStep('chooseSubject');
  };

  // Sync selectedClasses when subject changes
  useEffect(() => {
    if (selectedSubject) {
      const existingSubject = formData.subjects.find((item) => item.id === selectedSubject.subject.id);

      const initialSelectedClasses: { [key: string]: string[] } = {};

      if (existingSubject) {
        existingSubject.schools.forEach((school) => {
          initialSelectedClasses[school.id] = school.classes;
        });
      } else {
        // Initialize empty for all schools
        Object.keys(selectedSubject.schools).forEach((schoolLevel) => {
          Object.keys(selectedSubject.schools[schoolLevel]).forEach((schoolType) => {
            selectedSubject.schools[schoolLevel][schoolType].forEach((school) => {
              initialSelectedClasses[school.id] = [];
            });
          });
        });
      }

      setSelectedClasses(initialSelectedClasses);
    }
  }, [selectedSubject, formData.subjects]);

  return (
    <>
      {imagesLoading && <Loading />}
      {step === 'chooseSubject' && (
        <div>
          <p className="text-gray-600 mb-4">Clicca su una materia per scegliere le scuole e le classi</p>

          <div className="bg-white p-3 sm:p-4 lg:p-6 rounded-lg sm:rounded-xl w-full grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4 lg:gap-6 overflow-y-auto">
            {subjects.map((subjectInfo) => {
              const activeSubject = formData.subjects.find((item) => item.id === subjectInfo.subject.id);
              const activeSchoolsCount = activeSubject ? activeSubject.schools.filter((school) => school.classes.length > 0).length : 0;

              return (
                <div
                  key={subjectInfo.subject.id}
                  onClick={() => handleSelectSubject(subjectInfo)}
                  className="p-2 sm:p-3 lg:p-4 border rounded-lg bg-gray-50 shadow-sm h-28 sm:h-32 lg:h-36 flex flex-col items-center justify-center cursor-pointer relative hover:shadow-md transition-all duration-200 hover:scale-105">
                  {images[subjectInfo.subject.name] && (
                    <img src={images[subjectInfo.subject.name]} alt={subjectInfo.subject.name} className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16" />
                  )}
                  <h3 className="font-semibold text-center text-xs sm:text-sm lg:text-base mt-1 sm:mt-2">{subjectInfo.subject.name}</h3>
                  {activeSchoolsCount > 0 && (
                    <span className="absolute top-1 right-1 bg-fpcred text-white text-xs font-bold px-2 py-1 rounded-full">{activeSchoolsCount}</span>
                  )}
                </div>
              );
            })}
          </div>

          {errors.subjects && <p className="mt-4 text-sm text-red-500">{errors.subjects}</p>}
        </div>
      )}

      {step === 'chooseSchool' && selectedSubject && (
        <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-lg sm:rounded-xl w-full">
          <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                {images[selectedSubject.subject.name] && (
                  <img src={images[selectedSubject.subject.name]} alt={selectedSubject.subject.name} className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16" />
                )}
                <h3 className="font-bold text-fpcred text-base sm:text-lg lg:text-xl">{selectedSubject.subject.name}</h3>
              </div>

              {Object.keys(selectedSubject.schools).length === 0 ? (
                <p className="text-gray-500 italic">Nessuna scuola disponibile per questa materia.</p>
              ) : (
                Object.keys(selectedSubject.schools).map((schoolLevel) => (
                  <div className="flex flex-col gap-y-3 sm:gap-y-4 mb-4 sm:mb-6" key={schoolLevel}>
                    <h4 className="text-base sm:text-lg font-semibold text-gray-800 mb-2">{getLevelDescription(schoolLevel)}</h4>

                    {Object.keys(selectedSubject.schools[schoolLevel]).map((schoolType) => (
                      <div className="border-2 border-gray-300 p-3 sm:p-4 rounded-lg shadow-md ml-2 sm:ml-3 lg:ml-4 mb-3" key={schoolType}>
                        <h5 className="text-sm sm:text-base font-semibold text-gray-700 mb-2 sm:mb-3">{getSchoolTypeName(schoolType)}</h5>

                        {selectedSubject.schools[schoolLevel][schoolType].map((school) => (
                          <div className="ml-2 sm:ml-3 lg:ml-4 mb-3 sm:mb-4" key={school.id}>
                            <h6 className="text-sm sm:text-base font-medium text-gray-800 mb-2">{school.name}</h6>
                            <div className="flex flex-wrap gap-2 sm:gap-3 lg:gap-4">
                              {school.classes.map((schoolClass) => (
                                <button
                                  className={`py-1.5 px-3 sm:py-2 sm:px-4 rounded-md text-xs sm:text-sm font-medium transition-all duration-200 ${
                                    selectedClasses[school.id]?.includes(schoolClass) ? 'bg-fpcred text-white hover:bg-buttonhover' : 'bg-fpcwhite hover:bg-gray-200'
                                  }`}
                                  key={schoolClass}
                                  onClick={() => handleSelectYears(school.id, schoolClass)}>
                                  {schoolClass}
                                </button>
                              ))}
                              <button
                                className={`py-1.5 px-3 sm:py-2 sm:px-4 rounded-md text-xs sm:text-sm font-medium border-2 transition-all duration-200 whitespace-nowrap ${
                                  selectedClasses[school.id]?.length === school.classes.length
                                    ? 'bg-fpcpink border-gray-400 text-fpcred hover:bg-fpcred hover:text-white'
                                    : 'bg-white border-gray-400 text-black hover:bg-gray-100'
                                }`}
                                onClick={() => handleSelectAllYears(school.id, school.classes)}>
                                <span className="hidden sm:inline">{selectedClasses[school.id]?.length === school.classes.length ? 'Deseleziona tutti' : 'Seleziona tutti'}</span>
                                <span className="sm:hidden">{selectedClasses[school.id]?.length === school.classes.length ? 'Deseleziona' : 'Tutti'}</span>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                ))
              )}

          <div className="flex w-full justify-center items-center mt-4 sm:mt-6 lg:mt-8 pt-4 border-t border-gray-200">
            <button
              className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 text-white text-sm sm:text-base lg:text-lg font-semibold bg-fpcred rounded-lg sm:rounded-xl hover:bg-buttonhover transition-all duration-200 transform hover:scale-105 shadow-lg"
              onClick={handleBack}>
              Conferma scuole
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default SubjectsSelectionStep;
